/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import Decimal from 'decimal.js';
import {
	formatNumberWithSpaces,
	formatTwoDecimals,
} from '@utils/formats';
import { getUnitLabel } from '@utils/units';
import { getCurrencyUnit } from '@utils/currency-units';
import { NOT_APPLICABLE } from '@config/constants/common';
import { randomId } from '@mantine/hooks';
import isNil from 'lodash/isNil';
import {
	TApiAdditionalityProject,
	TApiMeasurabilityProject,
	TApiResponseMixin,
	TParsedRowProject,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getOffsetById = (
	id: string
): Promise<TApiResponseMixin> => {
	return apiClient.get(`/offsets/${id}`);
};

export const useOffsetById = (id: string) => {
	const { data, isFetched, isFetching } = useQuery({
		queryKey: ['offset-by-id', id],
		queryFn: () => getOffsetById(id),
		enabled: !!id,
	});

	const updateMeasurabilities = (
		mesurabilities: TApiMeasurabilityProject[]
	) => {
		return mesurabilities.reduce(
			(
				acc: TParsedRowProject[],
				currentItem: TApiMeasurabilityProject
			) => {
				const quantityOffset = new Decimal(
					currentItem.quantity_aco2
				);

				const quantity = Decimal.div(
					quantityOffset.toNumber(),
					1000000
				);

				const price = new Decimal(
					currentItem.sellPrice_europertco2eq
				);

				const modifiedItem = {
					id: randomId(),
					name: !isNil(currentItem.name)
						? currentItem.name
						: NOT_APPLICABLE,
					quantity: `${formatTwoDecimals(
						quantity.toNumber()
					)} ${getUnitLabel('tco2e')}`,
					price:
						formatNumberWithSpaces(price.toNumber()) +
						getCurrencyUnit('EUR'),
					total: `${formatTwoDecimals(
						quantity.mul(price).toNumber()
					)}${getCurrencyUnit('EUR')}`,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);
	};

	const updateAdditionalities = (
		additionalities: TApiAdditionalityProject[]
	) => {
		return additionalities.reduce(
			(
				acc: TParsedRowProject[],
				currentItem: TApiAdditionalityProject
			) => {
				const modifiedItem = {
					id: randomId(),
					name: currentItem.name,
					quantity: '',
					price: '',
					total: `${formatTwoDecimals(!isNil(currentItem) && !isNil(currentItem.quantity_addi) ? currentItem.quantity_addi : 0)}${getCurrencyUnit('EUR')}`,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);
	};

	return {
		data: data ? data : [],
		additionalities:
			data && data.mixin?.additionalities
				? updateAdditionalities(
						data.mixin.additionalities
					)
				: [],
		measurabilities:
			data && data.mixin?.measurabilities
				? updateMeasurabilities(
						data.mixin.measurabilities
					)
				: [],
		isLoading: isFetching && !isFetched,
		isFetched: isFetched,
	};
};
